import { useState, useEffect, useRef, useMemo } from 'react'
import { getJuaApiKey, MODEL_ID_MAP, VARIABLE_MAP } from './useJuaForecast'

const JUA_BASE = 'https://query.jua.ai/v1/forecast/'

// Coordinates mapping (same as useJuaForecast)
const COORDS: Record<string, { lat: number; lon: number }> = {
  UK: { lat: 51.5, lon: -0.12 },
  'United Kingdom': { lat: 51.5, lon: -0.12 },
  Germany: { lat: 52.52, lon: 13.4 },
  Nordic: { lat: 59.33, lon: 18.07 },
  France: { lat: 48.86, lon: 2.35 },
  Spain: { lat: 40.42, lon: -3.7 },
  'Iberian Peninsula': { lat: 40.42, lon: -3.7 },
  Italy: { lat: 41.9, lon: 12.5 },
  Netherlands: { lat: 52.37, lon: 4.9 },
  Poland: { lat: 52.23, lon: 21.01 },
  Sweden: { lat: 59.33, lon: 18.07 },
  Norway: { lat: 59.91, lon: 10.75 },
  'Central Eastern Europe': { lat: 50.08, lon: 14.44 },
  Balkans: { lat: 44.79, lon: 20.47 },
  Berlin: { lat: 52.52, lon: 13.4 },
  London: { lat: 51.5, lon: -0.12 },
  Paris: { lat: 48.86, lon: 2.35 },
  Madrid: { lat: 40.42, lon: -3.7 },
  Rome: { lat: 41.9, lon: 12.5 },
  Amsterdam: { lat: 52.37, lon: 4.9 },
  Warsaw: { lat: 52.23, lon: 21.01 },
  Stockholm: { lat: 59.33, lon: 18.07 },
  Oslo: { lat: 59.91, lon: 10.75 },
  Munich: { lat: 48.14, lon: 11.58 },
  Hamburg: { lat: 53.55, lon: 9.99 },
  'North Sea': { lat: 56.0, lon: 3.0 },
  'Baltic Sea': { lat: 58.0, lon: 20.0 },
  Mediterranean: { lat: 38.0, lon: 15.0 },
}

export interface JuaObservationsResult {
  /** Hourly observation values indexed by negative hour offset (e.g., -48 to 0) */
  observations: Record<number, number>
  /** Per-model hindcast data indexed by negative hour offset */
  modelHindcasts: Record<string, Record<number, number>>
  hasRealData: boolean
  loading: boolean
  error: string | null
}

// Cache for observation data
const obsCache: Record<string, { data: any; timestamp: number }> = {}
const CACHE_TTL = 10 * 60 * 1000 // 10 minutes

/**
 * Fetches real observation/hindcast data from the Jua API.
 * Uses a past init_time to get verified forecast data as observation proxy.
 * Falls back gracefully if no API key or API unavailable.
 */
export function useJuaObservations(
  region: string,
  variable: string,
  modelNames: string[],
  enabled: boolean,
): JuaObservationsResult {
  const [observations, setObservations] = useState<Record<number, number>>({})
  const [modelHindcasts, setModelHindcasts] = useState<
    Record<string, Record<number, number>>
  >({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasRealData, setHasRealData] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const coords = COORDS[region]
  const juaVariable = VARIABLE_MAP[variable]
  const juaModels = useMemo(
    () => modelNames.map((n) => MODEL_ID_MAP[n]).filter(Boolean),
    [modelNames],
  )

  useEffect(() => {
    if (!enabled) return

    const apiKey = getJuaApiKey()
    if (!apiKey || !juaVariable || !coords || juaModels.length === 0) {
      setHasRealData(false)
      setLoading(false)
      return
    }

    // Use a past init_time (48h ago, rounded to nearest 6h cycle)
    const now = new Date()
    const pastInit = new Date(now)
    pastInit.setUTCHours(Math.floor(pastInit.getUTCHours() / 6) * 6, 0, 0, 0)
    pastInit.setUTCDate(pastInit.getUTCDate() - 2) // 48h ago
    const initTimeStr = pastInit.toISOString().replace(/\.\d{3}Z$/, 'Z')

    const cacheKey = `jua_obs_${juaModels.sort().join(',')}_${coords.lat}_${coords.lon}_${juaVariable}_${initTimeStr}`
    const cached = obsCache[cacheKey]
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      setObservations(cached.data.observations)
      setModelHindcasts(cached.data.modelHindcasts)
      setHasRealData(true)
      setLoading(false)
      setError(null)
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setLoading(true)
    setError(null)

    // Fetch hindcast data: forecast initialized 48h ago, up to 48h ahead
    const params = new URLSearchParams()
    params.set('format', 'json')
    params.set('init_time', initTimeStr)
    params.set('latitude', String(coords.lat))
    params.set('longitude', String(coords.lon))
    params.set('max_prediction_timedelta', '48')
    juaModels.forEach((m) => params.append('models', m))
    params.append('variables', juaVariable)

    const url = `${JUA_BASE}?${params.toString()}`

    fetch(url, {
      headers: {
        'X-API-Key': apiKey,
        Accept: 'application/json',
      },
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          if (res.status === 401 || res.status === 403) {
            throw new Error('Invalid Jua API key.')
          }
          throw new Error(`Jua API error: ${res.status}`)
        }
        return res.json()
      })
      .then((json: any[]) => {
        if (!Array.isArray(json) || json.length === 0) {
          throw new Error('No observation data returned')
        }

        const obsMap: Record<number, number[]> = {} // hour → values from all models (averaged for obs line)
        const hindcastMap: Record<string, Record<number, number>> = {}

        for (const row of json) {
          const modelId = row.model || row.models
          const displayName =
            Object.entries(MODEL_ID_MAP).find(
              ([, id]) => id === modelId,
            )?.[0] || modelId

          const predHour =
            typeof row.prediction_timedelta === 'number'
              ? row.prediction_timedelta
              : parseFloat(row.prediction_timedelta) || 0

          const value = row[juaVariable] ?? row.value ?? null
          if (value === null || value === undefined || isNaN(Number(value)))
            continue

          const numValue = Number(value)

          // Map prediction hours to negative offsets:
          // init was 48h ago, so predHour 0 = -48h from now, predHour 48 = now (h=0)
          const hourOffset = predHour - 48

          // Observations: average across all models
          if (!obsMap[hourOffset]) obsMap[hourOffset] = []
          obsMap[hourOffset].push(numValue)

          // Per-model hindcast
          if (!hindcastMap[displayName]) hindcastMap[displayName] = {}
          hindcastMap[displayName][hourOffset] = numValue
        }

        // Average observations across models
        const avgObs: Record<number, number> = {}
        for (const [hour, values] of Object.entries(obsMap)) {
          const h = Number(hour)
          avgObs[h] = +(
            values.reduce((a, b) => a + b, 0) / values.length
          ).toFixed(2)
        }

        const result = { observations: avgObs, modelHindcasts: hindcastMap }
        obsCache[cacheKey] = { data: result, timestamp: Date.now() }
        setObservations(avgObs)
        setModelHindcasts(hindcastMap)
        setHasRealData(true)
        setLoading(false)
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        console.error('Jua observations error:', err)
        setError(err.message)
        setHasRealData(false)
        setLoading(false)
      })

    return () => controller.abort()
  }, [juaModels.join(','), juaVariable, coords?.lat, coords?.lon, enabled])

  return { observations, modelHindcasts, hasRealData, loading, error }
}
